import { useState, useEffect } from "react";
import axios from "axios";
import { DashboardLayout, workerNavItems, teamLeaderNavItems } from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { 
  User, 
  Shield, 
  CreditCard, 
  Save, 
  Star, 
  Hash, 
  Phone, 
  Copy, 
  Check,
  Upload,
  FileText,
  Camera,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Trash2,
  Signature,
  IdCard,
  GraduationCap,
  MapPin,
  Eye,
  Car,
  Users,
  DollarSign,
  MessageSquare
} from "lucide-react";

// Import refactored components
import { PromotionRequestForm } from "./components/PromotionRequestForm";
import { IdentityTab } from "./components/IdentityTab";
import { DocumentsTab } from "./components/DocumentsTab";
import { ContractTab } from "./components/ContractTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WorkerProfile() {
  const { user, setUser } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [profileStatus, setProfileStatus] = useState(null);
  const [contract, setContract] = useState(null);
  const [signingContract, setSigningContract] = useState(false);
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [signatureName, setSignatureName] = useState("");
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  const [profile, setProfile] = useState({
    worker_code: "",
    skills: [],
    certifications: [],
    hourly_rate: 0,
    employment_type: "on-call",
    hms_card_status: "pending",
    bank_account: "",
    availability: {},
    is_available: false,
    average_rating: 0,
    total_ratings: 0,
    norwegian_id: "",
    norwegian_id_verified: false,
    d_number: "",
    d_number_verified: false,
    date_of_birth: "",
    nationality: "",
    phone: "",
    address: "",
    city: "",
    postal_code: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    profile_photo: "",
    documents: [],
    contract_signed: false,
    contract_signed_at: "",
  });
  const [newSkill, setNewSkill] = useState("");
  const [newCert, setNewCert] = useState("");
  const [autoSaving, setAutoSaving] = useState({});
  
  // Norwegian ID validation state
  const [idValidation, setIdValidation] = useState({ norwegian_id: null, d_number: null });
  const [validatingId, setValidatingId] = useState({ norwegian_id: false, d_number: false });

  useEffect(() => {
    fetchData();
  }, [user?.user_id]);

  const fetchData = async () => {
    if (!user?.user_id) return;
    
    try {
      const [profileRes, statusRes] = await Promise.all([
        axios.get(`${API}/workers/${user.user_id}/profile`),
        axios.get(`${API}/workers/profile-status`)
      ]);
      
      setProfile(profileRes.data);
      setProfileStatus(statusRes.data);
      
      // Fetch employment contract separately
      try {
        const contractRes = await axios.get(`${API}/employment-contracts/my`);
        setContract(contractRes.data?.contract || null);
      } catch (err) {
        // Contract may not exist yet
        setContract(null);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  // Auto-save function with debounce
  const autoSaveField = async (fieldName, value) => {
    setAutoSaving(prev => ({ ...prev, [fieldName]: true }));
    try {
      await axios.put(`${API}/workers/auto-save-field`, {
        field: fieldName,
        value: value
      });
      // Show subtle success indicator
      setTimeout(() => {
        setAutoSaving(prev => ({ ...prev, [fieldName]: false }));
      }, 500);
    } catch (error) {
      console.error(`Failed to auto-save ${fieldName}:`, error);
      setAutoSaving(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  // Debounce timer refs
  const debounceTimers = {};
  
  const handleAutoSaveChange = (fieldName, value, delay = 1000) => {
    // Update local state immediately
    setProfile(prev => ({ ...prev, [fieldName]: value }));
    
    // Clear existing timer for this field
    if (debounceTimers[fieldName]) {
      clearTimeout(debounceTimers[fieldName]);
    }
    
    // Set new timer for auto-save
    debounceTimers[fieldName] = setTimeout(() => {
      autoSaveField(fieldName, value);
    }, delay);
  };

  const handleSave = async () => {
    // Check if there's an invalid ID before saving
    const hasInvalidNorwegianId = profile.norwegian_id && profile.norwegian_id.length === 11 && idValidation.norwegian_id?.valid === false;
    const hasInvalidDNumber = profile.d_number && profile.d_number.length === 11 && idValidation.d_number?.valid === false;
    
    if (hasInvalidNorwegianId || hasInvalidDNumber) {
      toast.error("Please fix the invalid ID number before saving");
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.put(`${API}/workers/${user.user_id}/profile`, {
        skills: profile.skills,
        certifications: profile.certifications,
        hourly_rate: profile.hourly_rate,
        employment_type: profile.employment_type,
        bank_account: profile.bank_account,
        norwegian_id: profile.norwegian_id,
        d_number: profile.d_number,
        date_of_birth: profile.date_of_birth,
        nationality: profile.nationality,
        phone: profile.phone,
        address: profile.address,
        city: profile.city,
        postal_code: profile.postal_code,
        emergency_contact_name: profile.emergency_contact_name,
        emergency_contact_phone: profile.emergency_contact_phone,
        is_available: profile.is_available,
      });
      
      // Check if backend returned validation info
      if (response.data?.id_validation) {
        const validation = response.data.id_validation;
        if (validation.valid && validation.details?.birth_date) {
          toast.success(`Profile updated! Birth date auto-filled from ${validation.type === 'd_number' ? 'D-nummer' : 'Personnummer'}`);
        } else {
          toast.success("Profile updated!");
        }
      } else {
        toast.success("Profile updated!");
      }
      
      // If profile has required personal data and no contract exists, create one
      const hasPersonalData = profile.norwegian_id || profile.d_number;
      if (hasPersonalData && !contract) {
        try {
          const contractRes = await axios.post(`${API}/employment-contracts`);
          if (contractRes.data?.contract_id) {
            toast.success("Employment contract created! Please review and sign it.");
            // Refresh contract data
            const newContractRes = await axios.get(`${API}/employment-contracts/my`);
            setContract(newContractRes.data?.contract || null);
          }
        } catch (err) {
          // Contract might already exist
        }
      }
      
      fetchData();
    } catch (error) {
      // Check if it's a validation error from backend
      if (error.response?.data?.detail?.includes("Invalid ID")) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Failed to update profile");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAvailabilityToggle = async (checked) => {
    setProfile({ ...profile, is_available: checked });
    try {
      await axios.put(`${API}/workers/availability?is_available=${checked}`);
      toast.success(checked ? "You are now available for work!" : "Availability turned off");
    } catch (error) {
      setProfile({ ...profile, is_available: !checked });
      toast.error("Failed to update availability");
    }
  };

  const handleSubmitNorwegianId = async () => {
    if (!profile.norwegian_id || profile.norwegian_id.length !== 11) {
      toast.error("Norwegian ID must be 11 digits");
      return;
    }
    
    try {
      await axios.put(`${API}/workers/verify-norwegian-id`, {
        norwegian_id: profile.norwegian_id
      });
      toast.success("Norwegian ID submitted for verification");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit Norwegian ID");
    }
  };

  const handleSubmitDNumber = async () => {
    if (!profile.d_number || profile.d_number.length !== 11) {
      toast.error("D-number must be 11 digits");
      return;
    }
    
    try {
      await axios.put(`${API}/workers/verify-d-number`, {
        d_number: profile.d_number
      });
      toast.success("D-number submitted for verification");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit D-number");
    }
  };

  const handleUploadDocument = async (docType, file, slotName = null) => {
    if (!file) return;
    
    setUploadingDoc(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await axios.post(`${API}/workers/documents`, {
          document_type: docType,
          document_name: slotName ? `${file.name} (${slotName})` : file.name,
          slot_name: slotName,
          file: reader.result,
          filename: file.name
        });
        toast.success("Document uploaded successfully");
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to upload document");
      } finally {
        setUploadingDoc(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteDocument = async (documentId) => {
    if (!confirm("Are you sure you want to delete this document?")) return;
    
    try {
      await axios.delete(`${API}/workers/documents/${documentId}`);
      toast.success("Document deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const handleUploadPhoto = async (file) => {
    if (!file) return;
    
    setUploadingPhoto(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const response = await axios.post(`${API}/workers/profile-photo`, {
          file: reader.result
        });
        toast.success("Photo uploaded successfully");
        setProfile({ ...profile, profile_photo: response.data.url });
        // Update user context
        setUser({ ...user, picture: response.data.url });
        fetchData();
      } catch (error) {
        toast.error("Failed to upload photo");
      } finally {
        setUploadingPhoto(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSignContract = async () => {
    if (!signatureName.trim()) {
      toast.error("Please enter your full name as signature");
      return;
    }
    
    setSigningContract(true);
    try {
      await axios.post(`${API}/workers/sign-contract`, {
        signature: signatureName.trim()
      });
      toast.success("Contract signed successfully!");
      setContractDialogOpen(false);
      setSignatureName("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sign contract");
    } finally {
      setSigningContract(false);
    }
  };

  const copyWorkerCode = () => {
    navigator.clipboard.writeText(profile.worker_code);
    setCopiedCode(true);
    toast.success("Worker code copied!");
    setTimeout(() => setCopiedCode(false), 2000);
  };

  // Validate Norwegian ID or D-number in real-time
  const validateIdNumber = async (idNumber, idType) => {
    if (!idNumber || idNumber.length !== 11) {
      setIdValidation(prev => ({ ...prev, [idType]: null }));
      return;
    }
    
    setValidatingId(prev => ({ ...prev, [idType]: true }));
    try {
      const response = await axios.post(`${API}/validate-norwegian-id`, {
        id_number: idNumber
      });
      setIdValidation(prev => ({ ...prev, [idType]: response.data }));
      
      // Auto-fill date of birth if valid
      if (response.data.valid && response.data.details?.birth_date) {
        // Convert DD.MM.YYYY to YYYY-MM-DD for input field
        const [day, month, year] = response.data.details.birth_date.split('.');
        const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        setProfile(prev => ({ ...prev, date_of_birth: formattedDate }));
      }
    } catch (error) {
      setIdValidation(prev => ({ 
        ...prev, 
        [idType]: { 
          valid: false, 
          error: error.response?.data?.detail || "Validation failed" 
        } 
      }));
    } finally {
      setValidatingId(prev => ({ ...prev, [idType]: false }));
    }
  };

  // Handle ID input change with validation
  const handleIdChange = (value, idType) => {
    const cleanValue = value.replace(/\D/g, '').slice(0, 11);
    setProfile(prev => ({ ...prev, [idType]: cleanValue }));
    
    // Validate when 11 digits entered
    if (cleanValue.length === 11) {
      validateIdNumber(cleanValue, idType);
    } else {
      setIdValidation(prev => ({ ...prev, [idType]: null }));
    }
  };

  const addSkill = async () => {
    if (newSkill.trim() && !profile.skills.includes(newSkill.trim())) {
      const newSkills = [...profile.skills, newSkill.trim()];
      setProfile({ ...profile, skills: newSkills });
      setNewSkill("");
      // Auto-save skills
      await autoSaveField('skills', newSkills);
      toast.success("Skill added!");
    }
  };

  const removeSkill = async (skill) => {
    const newSkills = profile.skills.filter(s => s !== skill);
    setProfile({ ...profile, skills: newSkills });
    // Auto-save skills
    await autoSaveField('skills', newSkills);
  };

  const addCertification = async () => {
    if (newCert.trim() && !profile.certifications.includes(newCert.trim())) {
      const newCerts = [...profile.certifications, newCert.trim()];
      setProfile({ ...profile, certifications: newCerts });
      setNewCert("");
      // Auto-save certifications
      await autoSaveField('certifications', newCerts);
      toast.success("Certification added!");
    }
  };

  const removeCertification = async (cert) => {
    const newCerts = profile.certifications.filter(c => c !== cert);
    setProfile({ ...profile, certifications: newCerts });
    // Auto-save certifications
    await autoSaveField('certifications', newCerts);
  };

  const getDocumentIcon = (type) => {
    switch(type) {
      case 'passport': return <IdCard className="w-5 h-5" />;
      case 'driver_license': return <Car className="w-5 h-5" />;
      case 'hms_card': return <Shield className="w-5 h-5" />;
      case 'certificate': return <GraduationCap className="w-5 h-5" />;
      default: return <FileText className="w-5 h-5" />;
    }
  };

  const getDocumentLabel = (type) => {
    switch(type) {
      case 'passport': return 'Passport / ID Document';
      case 'driver_license': return 'Driver License';
      case 'hms_card': return 'HMS Card';
      case 'certificate': return 'Certificate';
      case 'id_card': return 'ID Card';
      default: return 'Other Document';
    }
  };

  // Determine nav items based on user role
  const navItems = user?.role === 'team_leader' ? teamLeaderNavItems : workerNavItems;

  if (loading) {
    return (
      <DashboardLayout navItems={navItems} title={t('profile.title')}>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={navItems} title={t('profile.title')}>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="worker-profile">
        
        {/* Profile Completion Status - Only show if contract not signed */}
        {profileStatus && !profileStatus.can_take_jobs && !profileStatus.requirements?.contract_signed && (
          <Card className="bg-yellow-500/10 border-yellow-500/30">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-8 h-8 text-yellow-400 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-yellow-400 mb-2">{t('contracts.completeProfileFirst')}</h3>
                  <p className="text-[#94A3B8] text-sm mb-4">
                    {t('common.required')}:
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {!profileStatus.requirements?.contract_signed && (
                      <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs">
                        {t('contracts.signContract')}
                      </span>
                    )}
                    {!profileStatus.requirements?.admin_approved && (
                      <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs">
                        {t('common.pending')} Admin Approval
                      </span>
                    )}
                  </div>
                  <Progress value={profileStatus.completion_percentage} className="h-2" />
                  <p className="text-xs text-[#94A3B8] mt-2">{profileStatus.completion_percentage}% {t('common.completed')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Show waiting for admin approval if contract is signed but not approved */}
        {profileStatus && !profileStatus.can_take_jobs && profileStatus.requirements?.contract_signed && !profileStatus.requirements?.admin_approved && !(contract?.hr_signed) && (
          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Clock className="w-6 h-6 text-blue-400" />
                <div>
                  <span className="text-blue-400 font-medium">Contract Signed! ✓</span>
                  <p className="text-[#94A3B8] text-sm">Waiting for HR to sign the contract.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Show ready to work when both parties signed */}
        {contract?.status === "fully_signed" && !profileStatus?.can_take_jobs && (
          <Card className="bg-green-500/10 border-green-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-green-400" />
                <div>
                  <span className="text-green-400 font-medium">Contract Fully Signed! ✓</span>
                  <p className="text-[#94A3B8] text-sm">You are ready to start working on projects.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {profileStatus?.can_take_jobs && (
          <Card className="bg-green-500/10 border-green-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-green-400" />
                <span className="text-green-400 font-medium">Profile Complete - You can apply for jobs!</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Employment Contract Card - Show when contract exists but not fully signed */}
        {contract && contract.status !== "fully_signed" && (
          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-blue-400 mb-2">Employment Contract</h3>
                  <p className="text-[#94A3B8] text-sm mb-4">
                    {!contract.worker_signed 
                      ? "Your employment contract is ready. Please review and sign it to continue."
                      : "Your contract has been signed. Waiting for HR/Admin approval."
                    }
                  </p>
                  <div className="flex flex-wrap gap-3 mb-4">
                    <span className={`px-3 py-1 rounded-full text-xs ${
                      contract.worker_signed 
                        ? "bg-green-500/20 text-green-400" 
                        : "bg-yellow-500/20 text-yellow-400"
                    }`}>
                      {contract.worker_signed ? "✓ You signed" : "⏳ Your signature needed"}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs ${
                      contract.hr_signed 
                        ? "bg-green-500/20 text-green-400" 
                        : "bg-gray-500/20 text-gray-400"
                    }`}>
                      {contract.hr_signed ? "✓ HR approved" : "⏳ HR approval pending"}
                    </span>
                  </div>
                  <a href="/worker/employment-contract">
                    <Button className="bg-blue-500 hover:bg-blue-600 text-white">
                      <FileText className="w-4 h-4 mr-2" />
                      {contract.worker_signed ? "View Contract" : "Review & Sign Contract"}
                    </Button>
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Worker Code & Availability */}
        <Card className="bg-gradient-to-br from-[#23F7DD]/10 to-[#4F46E5]/10 border-[#23F7DD]/30">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="relative group">
                  {profile.profile_photo ? (
                    <img 
                      src={profile.profile_photo.startsWith('http') 
                        ? profile.profile_photo 
                        : `${process.env.REACT_APP_BACKEND_URL}${profile.profile_photo}`}
                      alt="Profile"
                      className="w-20 h-20 rounded-full object-cover border-2 border-[#23F7DD]"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-[#23F7DD]/20 flex items-center justify-center border-2 border-[#23F7DD]/50">
                      <User className="w-10 h-10 text-[#23F7DD]" />
                    </div>
                  )}
                  <label 
                    className="absolute -bottom-1 -right-1 p-2 bg-[#23F7DD] rounded-full cursor-pointer hover:bg-[#70FFEF] transition-colors z-20 shadow-lg border-2 border-[#0A0A0A]"
                    data-testid="upload-photo-btn"
                  >
                    <Camera className="w-4 h-4 text-[#050505]" />
                    <input 
                      type="file" 
                      accept="image/*" 
                      className="hidden" 
                      onChange={(e) => handleUploadPhoto(e.target.files[0])}
                      disabled={uploadingPhoto}
                    />
                  </label>
                  {uploadingPhoto && (
                    <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-sm text-[#94A3B8]">Your Worker ID</p>
                  <div className="flex items-center gap-2">
                    <p className="text-2xl font-['Unbounded'] font-bold text-[#23F7DD]">
                      {profile.worker_code || "N/A"}
                    </p>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={copyWorkerCode}
                      className="text-[#94A3B8] hover:text-white"
                    >
                      {copiedCode ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-[#94A3B8]">Share this code with your team leader</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-black/30 rounded-lg">
                <div>
                  <p className="text-white font-medium">Available for Work</p>
                  <p className="text-sm text-[#94A3B8]">
                    {profile.is_available ? "You can be contacted" : "Turn on to receive offers"}
                  </p>
                </div>
                <Switch
                  checked={profile.is_available}
                  onCheckedChange={handleAvailabilityToggle}
                  className="data-[state=checked]:bg-[#23F7DD]"
                />
              </div>
            </div>

            {/* Rating section - always show stars */}
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-5 h-5 ${
                      star <= Math.round(profile.average_rating || 0)
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-gray-600"
                    }`}
                  />
                ))}
                <span className="text-white font-medium ml-2">
                  {(profile.average_rating || 0) > 0 
                    ? profile.average_rating.toFixed(1) 
                    : "No ratings yet"}
                </span>
                {(profile.total_ratings || 0) > 0 && (
                  <span className="text-[#94A3B8]">({profile.total_ratings} ratings)</span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="identity" className="space-y-6">
          <TabsList className="bg-[#0A0A0A] border border-white/10 p-1">
            <TabsTrigger value="identity" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-[#050505]">
              Identity & ID
            </TabsTrigger>
            <TabsTrigger value="documents" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-[#050505]">
              Documents
            </TabsTrigger>
            <TabsTrigger value="contract" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-[#050505]">
              Contract
            </TabsTrigger>
            <TabsTrigger value="skills" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-[#050505]">
              Skills
            </TabsTrigger>
            <TabsTrigger value="payment" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-[#050505]">
              Payment
            </TabsTrigger>
            {/* Show Promotion tab only for workers, not for team leaders */}
            {user?.role !== 'team_leader' && (
              <TabsTrigger value="promotion" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white bg-purple-500/20 text-purple-400 animate-pulse">
                ⭐ Become TL
              </TabsTrigger>
            )}
          </TabsList>

          {/* Identity Tab - Using refactored component */}
          <TabsContent value="identity" className="space-y-6">
            <IdentityTab 
              profile={profile}
              setProfile={setProfile}
              user={user}
              idValidation={idValidation}
              validatingId={validatingId}
              handleIdChange={handleIdChange}
              handleSubmitNorwegianId={handleSubmitNorwegianId}
              handleSubmitDNumber={handleSubmitDNumber}
            />
          </TabsContent>


          {/* Documents Tab */}
          {/* Documents Tab - Using refactored component */}
          <TabsContent value="documents" className="space-y-6">
            <DocumentsTab 
              profile={profile}
              uploadingDoc={uploadingDoc}
              handleUploadDocument={handleUploadDocument}
              handleDeleteDocument={handleDeleteDocument}
              onRefreshProfile={fetchData}
            />
          </TabsContent>

          {/* Contract Tab - Using refactored component */}
          <TabsContent value="contract" className="space-y-6">
            <ContractTab 
              profile={profile}
              contract={contract}
            />
          </TabsContent>

          <TabsContent value="skills" className="space-y-6">
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardHeader>
                <CardTitle className="text-white font-['Unbounded']">Skills</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    placeholder="Add a skill..."
                    className="bg-black/50 border-white/10 text-white placeholder:text-[#475569] focus:border-[#23F7DD]"
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSkill())}
                  />
                  <Button onClick={addSkill} className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]">
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {profile.skills?.map((skill) => (
                    <span
                      key={skill}
                      className="px-3 py-1 bg-[#23F7DD]/10 text-[#23F7DD] rounded-full text-sm flex items-center gap-2"
                    >
                      {skill}
                      <button onClick={() => removeSkill(skill)} className="hover:text-white">×</button>
                    </span>
                  ))}
                  {(!profile.skills || profile.skills.length === 0) && (
                    <p className="text-[#94A3B8] text-sm">No skills added yet</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#0A0A0A] border-white/10">
              <CardHeader>
                <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-[#23F7DD]" />
                  Certifications
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={newCert}
                    onChange={(e) => setNewCert(e.target.value)}
                    placeholder="Add a certification..."
                    className="bg-black/50 border-white/10 text-white placeholder:text-[#475569] focus:border-[#23F7DD]"
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCertification())}
                  />
                  <Button onClick={addCertification} className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]">
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {profile.certifications?.map((cert) => (
                    <span
                      key={cert}
                      className="px-3 py-1 bg-purple-500/10 text-purple-400 rounded-full text-sm flex items-center gap-2"
                    >
                      {cert}
                      <button onClick={() => removeCertification(cert)} className="hover:text-white">×</button>
                    </span>
                  ))}
                  {(!profile.certifications || profile.certifications.length === 0) && (
                    <p className="text-[#94A3B8] text-sm">No certifications added yet</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-6">
                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">Employment Type</Label>
                  <div className="p-3 bg-[#23F7DD]/10 border border-[#23F7DD]/30 rounded-lg">
                    <span className="text-[#23F7DD] font-medium">On-Call</span>
                    <p className="text-xs text-[#94A3B8] mt-1">You work based on project assignments when available</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Promotion Tab - Only visible for workers */}
          {user?.role !== 'team_leader' && (
            <TabsContent value="promotion" className="space-y-6">
              <Card className="bg-gradient-to-br from-purple-500/20 to-[#4F46E5]/20 border-purple-500/50">
                <CardHeader>
                  <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2 text-2xl">
                    <Star className="w-7 h-7 text-purple-400" />
                    {t('promotion.title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-white text-lg">
                    {t('promotion.subtitle')}
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-5 bg-white/5 rounded-xl border border-purple-500/30">
                      <h4 className="text-purple-400 font-bold mb-3 flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        {t('promotion.leadTeam')}
                      </h4>
                      <p className="text-[#94A3B8] text-sm">
                        {t('promotion.leadTeamDesc')}
                      </p>
                    </div>
                    
                    <div className="p-5 bg-white/5 rounded-xl border border-green-500/30">
                      <h4 className="text-green-400 font-bold mb-3 flex items-center gap-2">
                        <DollarSign className="w-5 h-5" />
                        {t('promotion.higherEarnings')}
                      </h4>
                      <p className="text-[#94A3B8] text-sm">
                        {t('promotion.higherEarningsDesc')}
                      </p>
                    </div>
                    
                    <div className="p-5 bg-white/5 rounded-xl border border-blue-500/30">
                      <h4 className="text-blue-400 font-bold mb-3 flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        {t('promotion.applyProjects')}
                      </h4>
                      <p className="text-[#94A3B8] text-sm">
                        {t('promotion.applyProjectsDesc')}
                      </p>
                    </div>
                    
                    <div className="p-5 bg-white/5 rounded-xl border border-cyan-500/30">
                      <h4 className="text-cyan-400 font-bold mb-3 flex items-center gap-2">
                        <MessageSquare className="w-5 h-5" />
                        {t('promotion.directComm')}
                      </h4>
                      <p className="text-[#94A3B8] text-sm">
                        {t('promotion.directCommDesc')}
                      </p>
                    </div>
                  </div>

                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <h4 className="text-yellow-400 font-medium mb-2">{t('promotion.requirements')}:</h4>
                    <ul className="text-sm text-[#94A3B8] space-y-1">
                      <li>✓ {t('promotion.req1')}</li>
                      <li>✓ {t('promotion.req2')}</li>
                      <li>✓ {t('promotion.req3')}</li>
                    </ul>
                  </div>

                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="w-full bg-purple-500 text-white hover:bg-purple-600 font-bold text-lg py-6" data-testid="request-promotion-btn">
                        <Star className="w-6 h-6 mr-2" />
                        {t('promotion.requestButton')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
                      <DialogHeader>
                        <DialogTitle className="font-['Unbounded']">{t('promotion.requestTitle')}</DialogTitle>
                      </DialogHeader>
                      <PromotionRequestForm onSuccess={() => toast.success(t('promotion.successMessage'))} />
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Payment Tab */}
          <TabsContent value="payment" className="space-y-6">
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardHeader>
                <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-[#23F7DD]" />
                  Payment Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">Bank Account Number</Label>
                  <Input
                    value={profile.bank_account || ""}
                    onChange={(e) => setProfile({ ...profile, bank_account: e.target.value })}
                    placeholder="1234 56 78901"
                    className="bg-black/50 border-white/10 text-white placeholder:text-[#475569] focus:border-[#23F7DD]"
                  />
                  <p className="text-xs text-[#94A3B8]">Your bank details are encrypted and secure</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold px-8"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <span className="w-5 h-5 border-2 border-[#050505] border-t-transparent rounded-full animate-spin"></span>
                Saving...
              </span>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Save Profile
              </>
            )}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
